import { useCallback } from "react";
import {
  useCreateCycle,
  useCycles,
  useUpdateCycle,
} from "@src/packages/cycles/hooks/useCycles";
import type { CycleType } from "@src/packages/cycles/hooks/useCycles";
import { findLikelyCycleMatches, type CanonicalCycle } from "./canonicalCycle";
import type { paths } from "@src/packages/openapi/schema";
import type { RequestBody } from "@src/packages/openapi/helpers";

type CycleCreateBody = RequestBody<paths["/{tenantId}/cycles"]["post"]>;
type CycleCreateInput = Omit<CycleCreateBody, "patientId">;
type CyclePatchBody = RequestBody<paths["/{tenantId}/cycles/{id}"]["patch"]>;

export type CanonicalCycleDraft = {
  cycleId?: string | null;
  /** Null means a historical imported type is preserved in rawCycleType only. */
  cycleType?: CycleType | null;
  status?: CycleCreateBody["status"];
  description?: string | null;
  protocol?: CycleCreateBody["protocol"];
  menstruationStartDate?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  rawCycleType?: string | null;
  rawProtocol?: string | null;
  provider?: string | null;
  clinic?: string | null;
  phases?: CycleCreateBody["phases"];
  outcome?: CycleCreateBody["outcome"];
  dayNotes?: CycleCreateBody["dayNotes"];
};

export type CanonicalCycleSaveResult = {
  cycle: CanonicalCycle;
  reusedExisting: boolean;
};

export class CanonicalCycleMatchRequiredError extends Error {
  constructor(readonly candidates: CanonicalCycle[]) {
    super("More than one existing cycle matches the entered information");
    this.name = "CanonicalCycleMatchRequiredError";
  }
}

export class CanonicalCycleSaveError extends Error {
  constructor(
    message: string,
    readonly cycleId: string | null,
    readonly completed: "none" | "cycle",
  ) {
    super(message);
    this.name = "CanonicalCycleSaveError";
  }
}

/**
 * `"closed"` is the right default for a cycle this boundary is about to create:
 * a cycle first learned about from a specimen, a past-cycle entry or a treatment
 * history is, by construction, one that already happened.
 *
 * It is the wrong default for an update, and silently so. A caller that PATCHes
 * an existing cycle to add one field — an outcome, a clinic name — states no
 * status, and this default then closed a cycle the patient was still in. That is
 * how validating frozen eggs ended an egg-freezing treatment mid-course.
 *
 * So the default belongs to creation only. On an update the field is sent when
 * the caller asked for it and omitted otherwise, which leaves the cycle's
 * lifecycle to the flows that own it.
 */
function cycleBodyFromDraft(
  draft: CanonicalCycleDraft,
  { isUpdate }: { isUpdate: boolean },
): CycleCreateInput {
  const body: CycleCreateInput = {};
  const status = isUpdate ? draft.status : (draft.status ?? "closed");
  if (status !== undefined) body.status = status;

  if (draft.cycleType) body.cycleType = draft.cycleType;
  if (draft.description !== undefined) body.description = draft.description;
  if (draft.protocol !== undefined) body.protocol = draft.protocol;
  if (draft.menstruationStartDate !== undefined) {
    body.menstruationStartDate = draft.menstruationStartDate;
  }
  if (draft.startDate !== undefined) body.startDate = draft.startDate;
  if (draft.endDate !== undefined) body.endDate = draft.endDate;
  if (draft.rawCycleType !== undefined) body.rawCycleType = draft.rawCycleType;
  if (draft.rawProtocol !== undefined) body.rawProtocol = draft.rawProtocol;
  if (draft.provider !== undefined) body.provider = draft.provider;
  if (draft.clinic !== undefined) body.clinic = draft.clinic;
  if (draft.phases !== undefined) body.phases = draft.phases;
  if (draft.outcome !== undefined) body.outcome = draft.outcome;
  if (draft.dayNotes !== undefined) body.dayNotes = draft.dayNotes;

  return body;
}

/**
 * Shared persistence boundary for every flow that can introduce a treatment
 * cycle. It resolves one existing cycle when the known identity is specific
 * enough and creates a canonical cycle otherwise. Specimen intake owns the
 * separate cohort/material persistence path.
 *
 * The specimen batch already has a server idempotency contract. Cycle POSTs
 * do not yet expose one in the generated API, so this boundary deliberately
 * retains the returned cycle ID in each caller's draft for safe retries and
 * does not claim to reconcile a lost response to a brand-new cycle POST.
 */
export function useCanonicalCycleIntake() {
  const { data: cycles = [], isLoading: isLoadingCycles } = useCycles();
  const createCycle = useCreateCycle();
  const updateCycle = useUpdateCycle();

  const save = useCallback(
    async (draft: CanonicalCycleDraft): Promise<CanonicalCycleSaveResult> => {
      if (isLoadingCycles) {
        throw new CanonicalCycleSaveError(
          "Cycle history is still loading. Please try again.",
          draft.cycleId ?? null,
          "none",
        );
      }

      const explicitCycleId = draft.cycleId ?? null;
      const explicitCycle = draft.cycleId
        ? cycles.find((cycle) => cycle.id === draft.cycleId)
        : undefined;
      const candidates =
        explicitCycleId || !draft.cycleType
          ? []
          : findLikelyCycleMatches(cycles, {
              cycleType: draft.cycleType,
              startDate: draft.startDate,
              clinic: draft.clinic,
              provider: draft.provider,
            });

      if (candidates.length > 1) {
        throw new CanonicalCycleMatchRequiredError(candidates);
      }

      const matchedCycle = explicitCycle ?? candidates[0] ?? null;
      const isUpdate = Boolean(explicitCycleId || matchedCycle);
      const body = cycleBodyFromDraft(draft, { isUpdate });
      let cycle: CanonicalCycle;

      try {
        if (explicitCycleId || matchedCycle) {
          const result = await updateCycle.mutateAsync({
            cycleId: explicitCycleId ?? matchedCycle!.id,
            data: body as CyclePatchBody,
          });
          const updated = result.data?.data;
          if (!updated) {
            throw new CanonicalCycleSaveError(
              "Cycle update returned no saved record",
              explicitCycleId ?? matchedCycle!.id,
              "none",
            );
          }
          cycle = updated;
        } else {
          const result = await createCycle.mutateAsync(body);
          const created = result.data?.data;
          if (!created) {
            throw new CanonicalCycleSaveError(
              "Cycle creation returned no saved record",
              null,
              "none",
            );
          }
          cycle = created;
        }
      } catch (error) {
        if (error instanceof CanonicalCycleSaveError) throw error;
        throw new CanonicalCycleSaveError(
          error instanceof Error ? error.message : "Failed to save cycle",
          explicitCycleId ?? matchedCycle?.id ?? null,
          "none",
        );
      }

      return {
        cycle,
        reusedExisting: Boolean(explicitCycleId || matchedCycle),
      };
    },
    [cycles, isLoadingCycles, createCycle, updateCycle],
  );

  return {
    save,
    isPending:
      isLoadingCycles || createCycle.isPending || updateCycle.isPending,
  };
}
